// Broadcaster audio engine: DSP chain -> WebCodecs Opus encode -> WS with seq continuity + reconnect.
import { WS_BASE } from "@/lib/api";

export function broadcasterSupported() {
  return typeof window !== "undefined" &&
    "AudioEncoder" in window &&
    "MediaStreamTrackProcessor" in window;
}

export class BroadcastEngine {
  constructor({ getToken, onState, onMeter }) {
    this.getToken = getToken;
    this.onState = onState || (() => {});
    this.onMeter = onMeter || (() => {});
    this.stopped = true;
    this.seq = 0;
    this.bytesWindow = [];
    this._encoderConfigured = false;
    this._reconnecting = false;
    this._backoff = 0;
    this._sampleRate = 48000;
  }

  async listDevices() {
    const devs = await navigator.mediaDevices.enumerateDevices();
    return devs.filter((d) => d.kind === "audioinput");
  }

  async start(deviceId, targetBitrate = 64000, initialGain = 1.0, isRecording = false) {
    this.targetBitrate = targetBitrate || 64000;
    this.isRecording = isRecording;
    this.stopped = false;
    this.seq = 0;
    this.bytesWindow = [];
    this._encoderConfigured = false;
    this._reconnecting = false;
    this._backoff = 0;
    this.onState("connecting");

    this.ctx = new (window.AudioContext || window.webkitAudioContext)({ sampleRate: 48000 });
    await this.ctx.resume();

    const audioConstraints = {
      echoCancellation: false,
      noiseSuppression: false,
      autoGainControl: false,
      channelCount: 1,
    };

    try {
      this.stream = await navigator.mediaDevices.getUserMedia({
        audio: deviceId && deviceId !== "default" ? { ...audioConstraints, deviceId: { ideal: deviceId } } : audioConstraints,
      });
    } catch (e) {
      console.warn("Targeted audio constraints failed, falling back to default mic:", e);
      this.stream = await navigator.mediaDevices.getUserMedia({ audio: true });
    }

    const rawTrack = this.stream.getAudioTracks()[0];
    if (rawTrack) {
      rawTrack.addEventListener("ended", () => {
        if (!this.stopped) this.onState("device-lost");
      });
    }

    const ctx = this.ctx;
    const src = ctx.createMediaStreamSource(this.stream);
    
    this.inputGain = ctx.createGain();
    this.inputGain.gain.value = typeof initialGain === "number" ? initialGain : 1.0;

    // 1. Female Speech High-Pass Filter (145 Hz cut-off: eliminates room rumble & low hums below female F0 range ~160-280Hz)
    const hp = ctx.createBiquadFilter();
    hp.type = "highpass";
    hp.frequency.value = 145;
    hp.Q.value = 0.707;

    // 2. Vocal Formant Presence EQ (+2.2 dB @ 3.2 kHz for female speech presence & crisp clarity)
    const presenceEq = ctx.createBiquadFilter();
    presenceEq.type = "peaking";
    presenceEq.frequency.value = 3200;
    presenceEq.Q.value = 1.2;
    presenceEq.gain.value = 2.2;

    // 3. High-Shelf Sibilance Softener (-1.5 dB @ 9 kHz to tame harsh vocal sibilants)
    const deEsser = ctx.createBiquadFilter();
    deEsser.type = "highshelf";
    deEsser.frequency.value = 9000;
    deEsser.gain.value = -1.5;

    // 4. Speech Dynamics Compressor (Speech-optimized threshold, 6dB soft knee, 3.5:1 ratio)
    const comp = ctx.createDynamicsCompressor();
    comp.threshold.value = -18;
    comp.knee.value = 6;
    comp.ratio.value = 3.5;
    comp.attack.value = 0.008;
    comp.release.value = 0.14;

    this.gate = ctx.createGain();
    this.gate.gain.value = 1;
    this.analyser = ctx.createAnalyser();
    this.analyser.fftSize = 1024;

    const dest = ctx.createMediaStreamDestination();
    dest.channelCount = 1;
    dest.channelCountMode = "explicit";

    // Female Vocal DSP Graph Topology:
    // Mic -> InputGain -> HPF 145Hz -> Presence 3.2kHz -> De-Esser -> Compressor -> Analyser -> Gate -> Destination
    src.connect(this.inputGain);
    this.inputGain.connect(hp);
    hp.connect(presenceEq);
    presenceEq.connect(deEsser);
    deEsser.connect(comp);
    comp.connect(this.analyser); // Tap detector before gate
    comp.connect(this.gate);
    this.gate.connect(dest);

    this._meterBuf = new Float32Array(this.analyser.fftSize);
    this._startMeter();

    this.encoder = new AudioEncoder({
      output: (chunk) => this._onChunk(chunk),
      error: (e) => {
        console.warn("AudioEncoder frame error:", e);
      },
    });

    const track = dest.stream.getAudioTracks()[0];
    this.processor = new MediaStreamTrackProcessor({ track });
    this.reader = this.processor.readable.getReader();

    const token = await this.getToken();
    await this._connect(token, isRecording);
    this._readLoop();
  }

  _onChunk(chunk) {
    if (this.ws && this.ws.readyState === 1) {
      const buffered = this.ws.bufferedAmount;

      // Adaptive Bitrate on Network Speed Drop
      if (buffered > 65536) {
        if (!this._throttled && this.encoder && this.encoder.state === "configured") {
          try {
            this.encoder.configure({
              codec: "opus",
              sampleRate: this._sampleRate || 48000,
              numberOfChannels: 1,
              bitrate: 24000, // Emergency bitrate boost for slow networks
            });
            this._throttled = true;
            console.warn("Network drop detected: throttled bitrate to 24kbps");
          } catch (e) {}
        }
        if (buffered > 131072) {
          // Drop non-essential frame on heavy congestion (>128KB queued)
          return;
        }
      } else if (buffered === 0 && this._throttled) {
        if (this.encoder && this.encoder.state === "configured") {
          try {
            this.encoder.configure({
              codec: "opus",
              sampleRate: this._sampleRate || 48000,
              numberOfChannels: 1,
              bitrate: this.targetBitrate || 64000,
            });
            this._throttled = false;
            console.info("Network speed recovered: restored target bitrate to", this.targetBitrate);
          } catch (e) {}
        }
      }
    }

    const buf = new Uint8Array(chunk.byteLength);
    chunk.copyTo(buf);
    this.seq = (this.seq + 1) >>> 0;

    const durUs = chunk.duration || 20000;
    const samples = Math.round((durUs * (this._sampleRate || 48000)) / 1e6);

    const frame = new Uint8Array(8 + buf.length);
    const dv = new DataView(frame.buffer);
    dv.setUint32(0, this.seq);
    dv.setUint32(4, samples);
    frame.set(buf, 8);

    if (this.ws && this.ws.readyState === 1) this.ws.send(frame);

    const now = performance.now();
    this.bytesWindow.push([now, buf.length]);
    while (this.bytesWindow.length && now - this.bytesWindow[0][0] > 1000) this.bytesWindow.shift();
  }

  async _readLoop() {
    while (!this.stopped) {
      let res;
      try { res = await this.reader.read(); } catch (e) { break; }
      if (res.done) break;
      const ad = res.value;

      if (!this._encoderConfigured && ad) {
        this._sampleRate = ad.sampleRate || this.ctx.sampleRate || 48000;
        const channels = ad.numberOfChannels || 1;
        const bps = this.targetBitrate || 64000;

        if (typeof AudioEncoder.isConfigSupported === "function") {
          const cfg = { codec: "opus", sampleRate: this._sampleRate, numberOfChannels: channels, bitrate: bps };
          AudioEncoder.isConfigSupported(cfg).then((res) => {
            if (res.supported && this.encoder && this.encoder.state !== "closed") {
              this.encoder.configure(cfg);
              this._encoderConfigured = true;
            }
          }).catch(() => {});
        } else {
          this.encoder.configure({
            codec: "opus",
            sampleRate: this._sampleRate,
            numberOfChannels: channels,
            bitrate: bps,
          });
          this._encoderConfigured = true;
        }
      }

      if (this.encoder && this.encoder.state === "configured") {
        try {
          this.encoder.encode(ad);
        } catch (e) {
          console.warn("AudioEncoder.encode failed:", e);
        }
      }
      try { ad.close(); } catch (e) {}
    }
  }

  _connect(token, isRecording = false) {
    return new Promise((resolve, reject) => {
      const recFlag = isRecording ? "&record=true" : "";
      const url = `${WS_BASE}/api/ws/broadcast?token=${encodeURIComponent(token)}${recFlag}`;
      const ws = new WebSocket(url);
      ws.binaryType = "arraybuffer";
      this.ws = ws;

      const timer = setTimeout(() => {
        try { ws.close(); } catch (e) {}
        reject(new Error("WebSocket connection timeout"));
      }, 8000);

      ws.onopen = () => {
        clearTimeout(timer);
        this._backoff = 0;
        this.onState("live");
        resolve();
      };
      ws.onerror = (e) => {
        clearTimeout(timer);
        reject(e);
      };
      ws.onclose = () => {
        clearTimeout(timer);
        if (this.ws !== ws) return; // Stale socket guard
        if (!this.stopped) {
          this.onState("reconnecting");
          this._reconnect();
        }
      };
    });
  }

  async _reconnect() {
    if (this.stopped || this._reconnecting) return;
    this._reconnecting = true;
    try {
      while (!this.stopped) {
        this._backoff = Math.min((this._backoff || 0) + 1, 5);
        const wait = Math.min(15000, 500 * Math.pow(2, this._backoff)) + Math.random() * 300;
        await new Promise((r) => setTimeout(r, wait));
        if (this.stopped) return;
        try {
          const t = await this.getToken();
          await this._connect(t);
          return;
        } catch (e) {}
      }
    } finally {
      this._reconnecting = false;
    }
  }

  _bitrate() {
    return this.bytesWindow.reduce((a, [, b]) => a + b, 0) * 8;
  }

  _startMeter() {
    let holdUntil = 0;
    let isGateOpen = false;

    const loop = () => {
      if (this.stopped) return;
      this.analyser.getFloatTimeDomainData(this._meterBuf);
      let sum = 0;
      for (let i = 0; i < this._meterBuf.length; i++) sum += this._meterBuf[i] * this._meterBuf[i];
      const rms = Math.sqrt(sum / this._meterBuf.length);

      const nowSec = this.ctx.currentTime;
      // Hysteresis: open at -38 dBFS (0.0126), close at -46 dBFS (0.005) with 350ms hold
      if (rms > 0.0126) {
        isGateOpen = true;
        holdUntil = nowSec + 0.35;
      } else if (rms < 0.005 && nowSec > holdUntil) {
        isGateOpen = false;
      }

      const gateVal = isGateOpen ? 1 : 0.05;
      try { this.gate.gain.setTargetAtTime(gateVal, nowSec, 0.02); } catch (e) {}

      this.onMeter(Math.min(1, rms * 4), this._bitrate());
      this._raf = requestAnimationFrame(loop);
    };
    loop();
  }

  setGain(factor) {
    if (this.inputGain && this.ctx) {
      try {
        const val = Math.max(0, Math.min(2.5, factor));
        this.inputGain.gain.setTargetAtTime(val, this.ctx.currentTime, 0.01);
      } catch (e) {}
    }
  }

  async stop() {
    this.stopped = true;
    try { if (this.ws && this.ws.readyState === 1) this.ws.send(JSON.stringify({ type: "end" })); } catch (e) {}
    if (this._raf) cancelAnimationFrame(this._raf);
    try { this.reader && this.reader.cancel(); } catch (e) {}
    try { this.encoder && this.encoder.state !== "closed" && this.encoder.close(); } catch (e) {}
    try { this.stream && this.stream.getTracks().forEach((t) => t.stop()); } catch (e) {}
    setTimeout(() => { try { this.ws && this.ws.close(); } catch (e) {} }, 200);
    try { this.ctx && this.ctx.state !== "closed" && (await this.ctx.close()); } catch (e) {}
    this.onState("stopped");
  }
}
